/**
 * Client-side "Implement script" templates for an API protection rule.
 *
 * The backend/SDK does not return these — the vendor console embeds the rule's
 * signing `key` (and the custom token `param`) directly into a per-language
 * token-generation snippet. `buildApiProtectionScripts(key, param)` returns the
 * four snippets shown under the "Implement script" tabs (Python / PHP / Go /
 * node.js), with the rule's key and parameter substituted in.
 *
 * The snippets are the vendor's official token generators verbatim; only the two
 * hardcoded placeholders below are swapped for the rule's actual values. Every
 * variant signs `hmac_sha256(path + unix_ts, key)`, url-encodes the base64
 * digest, and appends `?<param>=<ts>-<token>` (or `&…` when the path already has
 * a query string).
 */
export interface ApiProtectionScript {
  /** Tab label. */
  label: string;
  /** Stable tab value (also the language id). */
  lang: string;
  /** The full snippet, key/param already substituted. */
  code: string;
}

/** The literals the vendor bakes into the sample scripts (replaced per rule). */
const VENDOR_KEY = 'Vn3tw0rk-Ap1Key-2026-XyZ789abc';
const VENDOR_PARAM = 'vnis_tk';

const PYTHON = `import sys
import time
import hashlib
import base64
import urllib ,hmac
from urllib.parse import urlparse
def sha256sum(src):
  m = hashlib.sha256()
  m.update(src.encode("utf-8"))
  return m.digest()
def generate_url(path, sign_name="verify", ts=None):
  args_characters = "?"
  if any(c in args_characters for c in path):
    is_args = True
  else:
    is_args = False
  key = 'Vn3tw0rk-Ap1Key-2026-XyZ789abc'
  ts = int(time.mktime(time.strptime(ts, "%Y%m%d%H%M%S")) if ts else time.time())
  sign_name = "verify" if sign_name == "verify" else sign_name
  sstring = '%s%s' % (path, ts)
  aa = hmac.new(key.encode("utf-8"), sstring.encode("utf-8"), digestmod=hashlib.sha256).digest()
  urlencode = urllib.parse.quote_plus( base64.b64encode(hmac.new(key.encode("utf-8"), sstring.encode("utf-8"), digestmod=hashlib.sha256).digest()) )
  if is_args:
    request_url = '%s&%s=%s-%s' % (path, sign_name, ts, urlencode)
  else:
    request_url = '%s?%s=%s-%s' % (path, sign_name, ts, urlencode)
  print(request_url)
if __name__ == '__main__':
  if len(sys.argv) < 2:
      print('usage: python generate_url.py /foo/bar.html vnis_tk')
      sys.exit()
  args = sys.argv[1:]
  generate_url(*args)`;

const PHP = `<?php
  function generate_url($path, $sign_name){
    if (strpos($path, "?") !== false) {
      $is_args = true;
    } else {
      $is_args = false;
    }
    $key = 'Vn3tw0rk-Ap1Key-2026-XyZ789abc';
    $ts = time();
    $sstring = sprintf("%s%s",$path,$ts);
    $token  = urlencode(base64_encode(hash_hmac("sha256", $sstring, $key, true)));
    if ($is_args) {
        $request_url = sprintf("%s&%s=%s-%s", $path,$sign_name,$ts,$token);
    }else {
        $request_url = sprintf("%s?%s=%s-%s", $path,$sign_name,$ts,$token);
    }
    echo($request_url);
  }
  if ($argc < 2) {
    echo("usage: php generate_url.php /foo/bar.html vnis_tk");
    exit();
  }
  if ($argc == 3) {
    generate_url($argv[1],$argv[2]);
  }else{
    generate_url($argv[1], "verify");
  }
?>`;

const GO = `package main
import (
  "crypto/hmac"
  "crypto/sha256"
  "encoding/base64"
  "fmt"
  "net/url"
  "os"
  "strings"
  "time"
)
const (
  encodedKey = "Vn3tw0rk-Ap1Key-2026-XyZ789abc"
)
func generateURL(path string, signName string) {
  expiration := time.Now().Unix()
  isArgs := strings.Contains(path, "?")
  h := hmac.New(sha256.New, []byte(encodedKey))
  message := fmt.Sprintf("%s%d", path, expiration)
  h.Write([]byte(message))
  signature := h.Sum(nil)
  //signature := hex.EncodeToString(h.Sum(nil))
  var token string
  if isArgs {
    token = fmt.Sprintf("%s&%s=%d-%s", path, signName, expiration, url.QueryEscape(base64.StdEncoding.EncodeToString([]byte(signature))))
  } else {
    token = fmt.Sprintf("%s?%s=%d-%s", path, signName, expiration, url.QueryEscape(base64.StdEncoding.EncodeToString([]byte(signature))))
  }
  fmt.Printf("Token: %s", token)
}
func main() {
  argsWithoutProg := os.Args[1:]
  if len(argsWithoutProg) < 1 {
    fmt.Printf("usage:go run generate_url.go /foo/bar.html vnis_tk")
    return
  }
  if len(argsWithoutProg) == 1 {
    generateURL(argsWithoutProg[0], "verify")
  } else {
    generateURL(argsWithoutProg[0], argsWithoutProg[1])
  }
}`;

const NODE = `#! /usr/bin/env node
require('dotenv').config();
const crypto = require('crypto');
const urlencode = require('urlencode')
const key = 'Vn3tw0rk-Ap1Key-2026-XyZ789abc';
const filename = ''
const signName = 'vnis_tk'
function generateUrl(filename, signName) {
  const isArgs = filename.includes('?') ? '&' : '?'
  const ts = Math.floor(Date.now() / 1000)
  const hash = crypto.createHmac('sha256', key).update(filename + ts);
  const urlencodHash = urlencode(hash.digest('base64'))
  return filename + isArgs + signName + '=' + ts + '-' + urlencodHash
}
generateUrl(filename, signName)`;

/** Swap the vendor placeholders for this rule's key + custom parameter. */
function fill(template: string, key: string, param: string): string {
  return template.split(VENDOR_KEY).join(key || VENDOR_KEY).split(VENDOR_PARAM).join(param || VENDOR_PARAM);
}

export function buildApiProtectionScripts(key: string, param: string): ApiProtectionScript[] {
  return [
    { label: 'Python', lang: 'python', code: fill(PYTHON, key, param) },
    { label: 'PHP', lang: 'php', code: fill(PHP, key, param) },
    { label: 'Go', lang: 'go', code: fill(GO, key, param) },
    { label: 'node.js', lang: 'node', code: fill(NODE, key, param) },
  ];
}
